import React, { Key, useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './TradersCardList.scss'

import iconSearch from '../../../../../Assets/Icon/AdminTradersCard/mna2n321-ejwdxsi.svg'
import iconFilter from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-dt1dsj6.svg'
import iconExport from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-fjvjkh5.svg'
import iconPrev from '../../../../../Assets/Images/AdminTradersCard/mna2n321-kd3kkq4.png'
import iconNext from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-lk9t334.svg'

import { TimeConverter } from 'Helpers/TimeConverter'
import { plpType } from 'Models/plpType'
import plpService from 'Services/plpService'
import { HorizontalLoader } from 'Components'

type TradersCardStatusRoute = 'ALL' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED'

const TradersCardList: React.FC = () => {
  const { status: statusParam } = useParams()
  const activeStatus = (statusParam ?? 'ALL') as TradersCardStatusRoute

  const isValidStatus =
    activeStatus === 'ALL' ||
    activeStatus === 'PENDING_APPROVAL' ||
    activeStatus === 'APPROVED' ||
    activeStatus === 'REJECTED'

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(20)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [isSuspended] = useState<boolean | undefined>(undefined)

  const [q, setQ] = useState('')

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [rows, setRows] = useState<plpType[]>([])
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((r) => {
      const operator = `${r.customer?.firstName ?? ''} ${r.customer?.lastName ?? ''}`.trim()
      const haystack = [
        r.companyName,
        operator,
        r.customer?.msisdn,
        r.customer?.emailAddress,
        r.accountId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(needle)
    })
  }, [q, rows])

  const fetchApplications = async () => {
    setLoading(true)
    setErrorMessage(null)

    await plpService.index(pageNumber, sortOrder, activeStatus, isSuspended).then(
      (res) => {
        const data = res?.data.data
        setRows(data?.dataset)
        setPaginationData({
          pageNumber: Number(data?.pageNumber ?? pageNumber),
          pageSize: Number(data?.pageSize ?? pageSize),
          total: Number(data?.total ?? 0),
        })

        setLoading(false)
      },
      (e) => {
        setLoading(false)
        setRows([])
        setPaginationData({ pageNumber, pageSize, total: 0 })
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load applications')
      },
    )
  }

  useEffect(() => {
    if (!isValidStatus) return

    fetchApplications()
  }, [activeStatus, isValidStatus, isSuspended, pageNumber, pageSize, sortOrder])

  if (!isValidStatus) return <Navigate to='/blockchain/traders-card/ALL' replace />

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  return (
    <div className='admin-traderscard'>
      <div className='admin-traderscard__title'>TradersCard Applications</div>

      <div className='admin-traderscard__tabs'>
        <Link
          to='/blockchain/traders-card/ALL'
          className={[
            'admin-traderscard__tab',
            activeStatus === 'ALL' ? 'admin-traderscard__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          ALL
        </Link>
        <Link
          to='/blockchain/traders-card/PENDING_APPROVAL'
          className={[
            'admin-traderscard__tab',
            activeStatus === 'PENDING_APPROVAL' ? 'admin-traderscard__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          PENDING_APPROVAL
        </Link>
        <Link
          to='/blockchain/traders-card/APPROVED'
          className={[
            'admin-traderscard__tab',
            activeStatus === 'APPROVED' ? 'admin-traderscard__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          APPROVED
        </Link>
        <Link
          to='/blockchain/traders-card/REJECTED'
          className={[
            'admin-traderscard__tab',
            activeStatus === 'REJECTED' ? 'admin-traderscard__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          REJECTED
        </Link>
      </div>

      <div className='admin-traderscard__toolbar'>
        <div className='admin-traderscard-search'>
          <img src={iconSearch} alt='' className='admin-traderscard-search__icon' />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className='admin-traderscard-search__input'
            placeholder='Search Traders Card No or User'
          />
        </div>

        <button type='button' className='admin-traderscard-button'>
          <img src={iconFilter} alt='' className='admin-traderscard-button__icon' />
          <span>Filters</span>
        </button>

        <button type='button' className='admin-traderscard-button'>
          <img src={iconExport} alt='' className='admin-traderscard-button__icon' />
          <span>Export</span>
        </button>
      </div>

      <div className='admin-traderscard-table'>
        <div className='table-responsive'>
          <table className='admin-traderscard-table__native'>
            <thead>
              <tr
                className='admin-traderscard-table__head
                   admin-traderscard-table__grid--approved'
              >
                <th>S/N</th>
                <th>Company</th>
                <th>Customer</th>
                <th>CAC Number</th>
                <th>CompanyGA</th>
                <th>Capacity</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody className='admin-traderscard-table__body'>
              {loading && (
                <tr
                  className={[
                    'admin-traderscard-table__row',
                    activeStatus === 'APPROVED'
                      ? 'admin-traderscard-table__grid--approved'
                      : 'admin-traderscard-table__grid--pending',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <td className='admin-traderscard-table__cell text-center' colSpan={8}>
                    <HorizontalLoader></HorizontalLoader>
                  </td>
                </tr>
              )}

              {!loading && errorMessage && (
                <tr
                  className={[
                    'admin-traderscard-table__row',
                    activeStatus === 'APPROVED'
                      ? 'admin-traderscard-table__grid--approved'
                      : 'admin-traderscard-table__grid--pending',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <td className='admin-traderscard-table__cell' colSpan={8}>
                    {errorMessage}
                  </td>
                </tr>
              )}

              {!loading && !errorMessage && rows.length === 0 && (
                <tr
                  className={[
                    'admin-traderscard-table__row',
                    activeStatus === 'APPROVED'
                      ? 'admin-traderscard-table__grid--approved'
                      : 'admin-traderscard-table__grid--pending',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <td className='admin-traderscard-table__cell' colSpan={8}>
                    No applications found
                  </td>
                </tr>
              )}

              {!loading &&
                !errorMessage &&
                filteredRows.map((r: plpType, index: Key) => (
                  <tr
                    key={r.id}
                    className='admin-traderscard-table__row admin-traderscard-table__grid--approved'
                  >
                    <td>{(pageNumber - 1) * paginationData.pageSize + Number(index) + 1}</td>
                    <td className='admin-traderscard-table__cell'>{r.companyName ?? '-'}</td>
                    <td className='admin-traderscard-table__cell'>
                      {`${r.customer?.firstName ?? ''} ${r.customer?.lastName ?? ''}`.trim() || '-'}
                    </td>

                    <td className='admin-traderscard-table__cell'>{r.cacNumber}</td>
                    <td className='admin-traderscard-table__cell'>{r.companyLGA}</td>

                    <td className='admin-traderscard-table__cell'>{r.dailySalesCapacity}</td>

                    <td className='admin-traderscard-table__cell'>
                      {TimeConverter(r.createdAtISO8601)}
                    </td>

                    <td className='admin-traderscard-table__cell'>
                      <Link
                        to={`/blockchain/traders-card/${activeStatus}/${r.id}`}
                        className='admin-traderscard-table__action'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className='admin-traderscard-pagination'>
          <button
            type='button'
            className='admin-traderscard-pagination__button'
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || loading}
          >
            <img src={iconPrev} alt='' className='admin-traderscard-pagination__icon' />
            <span>Previous</span>
          </button>
          <div className='admin-traderscard-pagination__numbers'>
            Page {pageNumber} of {totalPages}
          </div>
          <button
            type='button'
            className='admin-traderscard-pagination__button'
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber >= totalPages || loading}
          >
            <span>Next</span>
            <img src={iconNext} alt='' className='admin-traderscard-pagination__icon' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default TradersCardList
